/**
 * LandingPage — Public marketing/sales site at `/`.
 *
 * 13-module showcase + AI section + IL compliance trust strip + bottom CTA.
 * Hero CTA and bottom CTA both link to `/start-discovery` (self-serve flow).
 * Contact form posts to `/api/tenants/contact/` (delivers to SALES_NOTIFY_EMAILS).
 *
 * Key state: activeModule (which module's features show in the right panel),
 *            contactForm, isSubmitting, isModuleDetailOpen.
 * Routes referenced: /login, /start-discovery, /share/* anchors.
 *
 * Touch carefully — every change here is visible to prospects. Always smoke-test
 * the modules selector + bottom CTA after edits.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  CheckCircle2, 
  Users, 
  GraduationCap, 
  DollarSign, 
  Bus, 
  Clock, 
  Shield, 
  BarChart3,
  Heart,
  FileText,
  Zap,
  Phone,
  ChevronRight,
  Building2,
  Briefcase,
  Calculator,
  ClipboardCheck,
  MapPinned,
  UserCheck,
  CalendarCheck,
  Search,
  FileSignature,
  ListChecks,
  TrendingUp,
  Globe,
  Award,
  Layers,
  X,
  ArrowLeft,
  Brain,
  UserPlus,
  Plug,
  Sparkles,
  Stethoscope,
  PiggyBank,
  Lock,
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeModule, setActiveModule] = useState('attendance');
  const [contactForm, setContactForm] = useState({
    first_name: '', last_name: '', email: '', district_name: '', student_count: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showBackButton, setShowBackButton] = useState(false);
  const [lastPage, setLastPage] = useState(null);

  // Inactivity timer for back button (120 seconds)
  useEffect(() => {
    if (!user) return;

    // Get the previous page from navigation state
    const previousPath = location.state?.from;
    
    if (previousPath && previousPath !== '/') {
      setLastPage(previousPath);
      setShowBackButton(true);

      let timeoutId;
      let activityTimeoutId;

      const resetTimer = () => {
        // Clear existing timeout
        if (activityTimeoutId) {
          clearTimeout(activityTimeoutId);
        }

        // Set new timeout for 120 seconds
        activityTimeoutId = setTimeout(() => {
          setShowBackButton(false);
          setLastPage(null);
        }, 120000); // 120 seconds
      };

      // Start initial timer
      resetTimer();

      // Reset timer on user activity
      const handleActivity = () => {
        resetTimer();
      };

      // Listen for user activity
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('scroll', handleActivity);
      window.addEventListener('click', handleActivity);

      // Cleanup
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (activityTimeoutId) clearTimeout(activityTimeoutId);
        window.removeEventListener('mousemove', handleActivity);
        window.removeEventListener('keydown', handleActivity);
        window.removeEventListener('scroll', handleActivity);
        window.removeEventListener('click', handleActivity);
      };
    }
  }, [user, location.state]);

  const handleBackToDashboard = () => {
    if (lastPage) {
      navigate(lastPage);
    } else {
      navigate('/dashboard');
    }
  };

  const dismissBackButton = () => {
    setShowBackButton(false);
    setLastPage(null);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.first_name || !contactForm.last_name || !contactForm.email) {
      toast.error('Please fill in your name and email.');
      return;
    }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API}/api/contact-requests/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.email_sent) {
        setSubmitted(true);
        toast.success('Thank you! Your request has been sent.');
      } else {
        toast.error(data.error || 'Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Unable to submit right now. Please email us at info@apsdatatechnologies.com');
    } finally {
      setSubmitting(false);
    }
  };

  const ACTIVE_MODULES = ['attendance', 'hiring'];

  const modules = [
    {
      id: 'attendance',
      name: 'Time & Attendance',
      icon: Clock,
      color: 'cyan',
      description: 'AI-powered time tracking, FMLA, scheduling, and ISBE compliance',
      features: [
        'Geofenced Mobile + Badge Clock-In',
        'FMLA Case Management',
        'Substitute Calling (Waterfall AI)',
        'Smart Savings Dashboard (AI)',
        'Absence Forecasting (AI)',
        'BIPA Compliance Workflows',
        'ISBE State Reports (8 types)',
        'Device Integrations (Kronos, HID, Frontline)',
      ],
    },
    {
      id: 'hiring',
      name: 'Hiring & Onboarding',
      icon: Briefcase,
      color: 'indigo',
      description: 'End-to-end recruitment with structured guided onboarding',
      features: [
        '7-Stage Drag-and-Drop Pipeline',
        'Job Requisitions & Approvals',
        'Interview Scheduling & Reminders',
        'Background Checks (Castle Branch, Truescreen, Checkr)',
        'Offer Letter Generation & Tracking',
        'Union Contract Rules Engine',
        'Guided New-Hire Checklist',
        'Export to Tyler Munis / Skyward',
      ],
    },
    {
      id: 'hr',
      name: 'Human Resources',
      icon: Users,
      color: 'blue',
      description: '15-module HRMS — records, evaluations, certifications, AI tools',
      features: [
        'Employee Records & Reporting Hierarchy',
        'Position Control & FTE Management',
        'Performance Evaluations',
        'Professional Development Tracking',
        'Certifications & License Compliance',
        'Salary Schedules + Seniority',
        'Total Compensation Statements',
        'AI HR Tools (job descriptions, eval drafts)',
      ],
    },
    {
      id: 'payroll',
      name: 'Payroll',
      icon: Calculator,
      color: 'amber',
      description: 'Multi-schedule processing with TRS/IMRF reporting',
      features: [
        'Bi-weekly + Semi-monthly + Monthly',
        'TRS / IMRF / SURS Integration',
        'Direct Deposit & Tax Compliance',
        'Employee Contract Management',
        'Compliance Reports (ACA 1094-C, W-2)',
        'Pay Stubs Self-Service',
      ],
    },
    {
      id: 'benefits',
      name: 'Benefits',
      icon: Heart,
      color: 'rose',
      description: 'Concierge-driven enrollment, COBRA, ACA, and life events',
      features: [
        'Health Concierge (AI plan recommendations)',
        'Retirement Concierge (403(b) / 457(b))',
        'Open Enrollment Workflow',
        'Life Events (marriage, birth, divorce)',
        'Dependent Management',
        'COBRA Administration',
        'ACA Compliance & 1095-C Reporting',
      ],
    },
    {
      id: 'finance',
      name: 'Finance & Accounting',
      icon: DollarSign,
      color: 'green',
      description: 'Fund accounting, purchasing, and district-wide budgeting',
      features: [
        'Fund Accounting (governmental)',
        'General Ledger',
        'Requisitions & Purchase Orders',
        'Vendor & Invoice Management',
        'Budget Planning & Tracking',
        'Financial Reporting',
      ],
    },
    {
      id: 'sis',
      name: 'Student Information',
      icon: GraduationCap,
      color: 'purple',
      description: 'Full SIS with IEP, 504, health records, and family portals',
      features: [
        'IEP & 504 Plan Management',
        'Master Schedule Builder',
        'Student Health Records',
        'Custom Forms (Erin\'s Law, photo release)',
        'Teacher Portal',
        'Student & Parent Portals',
        'Superintendent Portal',
      ],
    },
    {
      id: 'transportation',
      name: 'Transportation',
      icon: Bus,
      color: 'orange',
      description: '16-module fleet ops with telemetry, routing, and parent tracking',
      features: [
        'Live GPS Telemetry',
        'AI Route Optimization',
        'Walk Zones & Special Needs Transport',
        'Stop Arm Violations',
        'Predictive Maintenance (AI)',
        'Fuel Analytics',
        'Parent Bus Tracker app',
        'Driver Substitute Pool',
      ],
    },
    {
      id: 'revenue',
      name: 'Revenue Programs',
      icon: TrendingUp,
      color: 'emerald',
      description: 'Turn district assets into revenue — facilities, events, programs',
      features: [
        'Facility Booking & Calendar',
        'Event Ticketing',
        'Enrichment Programs',
        'Childcare Management',
        'Career Pathways',
        'Medicaid Reimbursement',
        'Revenue Analytics Dashboard',
      ],
    },
    {
      id: 'grants',
      name: 'Grants Management',
      icon: FileText,
      color: 'teal',
      description: 'Find, write, and track grants with AI assistance',
      features: [
        'Grant Discovery (federal + state + private)',
        'AI Grant Writer (drafts in minutes)',
        'Application Tracking',
        'Deadline Calendar',
        'Deliverables & Progress Reports',
      ],
    },
    {
      id: 'substitutes',
      name: 'Substitute Marketplace',
      icon: UserPlus,
      color: 'violet',
      description: 'SubConnect AI fills classrooms when traditional pools fail',
      features: [
        'SubConnect AI Matching',
        'Auto Call-Out (waterfall + parallel)',
        'Active Assignment Tracking',
        'Cross-District Substitute Pool',
        'Fill Rate Analytics',
      ],
    },
    {
      id: 'compliance',
      name: 'Compliance & Training',
      icon: Shield,
      color: 'slate',
      description: 'Mandated reporter, BIPA, FERPA — automated tracking',
      features: [
        'Mandated Reporter Training Tracking',
        'Bloodborne Pathogens Compliance',
        'BIPA Workflow & Consent Logging',
        'FERPA Records Custodian Tools',
        'Interactive Tours (12+ modules)',
        'District Services Hub',
      ],
    },
    {
      id: 'integrations',
      name: 'Integrations & Identity',
      icon: Plug,
      color: 'blue',
      description: 'SSO, AD sync, time-clock vendors, and payroll exports',
      features: [
        'Microsoft Entra ID (OIDC) + SAML 2.0',
        'On-Prem LDAP / Active Directory Sync',
        'SFTP CSV-Drop for Time Clocks',
        'Webhooks for Kronos, HID, Frontline',
        'Pilot Discovery Kit (69 questions)',
        'Identity Setup Wizard',
      ],
    },
  ];

  const activeModuleData = modules.find(m => m.id === activeModule);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
                <span className="text-white font-bold text-xl">K</span>
              </div>
              <span className="text-slate-900 text-xl font-bold">K12 ERP</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#modules" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Modules</a>
              <a href="#ai" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">AI</a>
              <a href="#il-districts" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">IL Compliance</a>
              <a href="#why-us" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Why K12 ERP</a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">Contact</a>
            </div>

            <div className="flex items-center gap-3">
              <Button data-testid="landingpage-sign-in-btn" variant="ghost" onClick={() => navigate('/login')} className="text-slate-600">
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Back to Dashboard Banner - Shows for 120 seconds after logged-in user navigates to homepage */}
      {user && showBackButton && (
        <div className="fixed top-16 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white z-40 shadow-lg animate-in slide-in-from-top duration-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">
                  You're viewing the homepage.
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button data-testid="landingpage-back-to-dashboard-btn" 
                  onClick={handleBackToDashboard}
                  size="sm"
                  className="bg-white text-blue-600 hover:bg-blue-50 font-medium"
                >
                  Back to Dashboard
                </Button>
                <button data-testid="landingpage-dismiss-btn" 
                  onClick={dismissBackButton}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section — Compact, high-impact */}
      <section className={`pt-24 pb-6 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden ${user && showBackButton ? 'mt-14' : ''}`}>
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-medium">
                <Zap className="h-3.5 w-3.5" />
                End-to-End ERP for K-12 Education
              </div>
              
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                  K-12 Employee
                  <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent"> Resource Planning</span>
                </h1>
                <p className="mt-2 text-sm text-slate-500 font-medium tracking-wide uppercase">Complete District Operations Platform</p>
              </div>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                The complete ERP platform built exclusively for K-12 districts.
                <strong className="text-slate-900"> 13 integrated modules. 150+ sub-modules. AI throughout.</strong>{' '}
                Manage hiring, time tracking, payroll, benefits, finance, transportation, grants, and more from a single system.
              </p>

              <div className="flex flex-wrap gap-3">
                <Button
                  size="lg"
                  className="h-12 px-6 text-base bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-xl shadow-indigo-600/20 font-medium"
                  onClick={() => navigate('/start-discovery')}
                  data-testid="hero-start-discovery-btn"
                >
                  Start Free Discovery
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button data-testid="landingpage-see-solutions-btn" size="lg" variant="outline" className="h-12 px-6 text-base border-slate-300"
                  onClick={() => document.getElementById('modules')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  See Solutions
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Self-serve, 15-minute structured questionnaire. No signup required.
                <a href="mailto:info@apsdatatechnologies.com?cc=harish@apsdatatechnologies.com" className="text-blue-600 hover:underline ml-1">
                  Or email us directly →
                </a>
              </p>
            </div>

            <div className="relative hidden lg:block">
              <div className="absolute -top-6 -right-6 w-56 h-56 bg-blue-100 rounded-full blur-3xl opacity-60" />
              <div className="absolute -bottom-6 -left-6 w-56 h-56 bg-cyan-100 rounded-full blur-3xl opacity-60" />
              
              <div className="relative bg-white rounded-2xl shadow-2xl shadow-slate-200/50 p-3 border border-slate-100">
                <img 
                  src="https://images.unsplash.com/photo-1771923082503-0a3381c46cef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBkYXNoYm9hcmQlMjBsYXB0b3B8ZW58MHx8fHwxNzc1NDI4NTQwfDA&ixlib=rb-4.1.0&q=85&w=800"
                  alt="K12 ERP Dashboard"
                  className="rounded-xl w-full object-cover"
                  style={{ maxHeight: '320px' }}
                />
                
                {/* Floating Cards */}
                <div className="absolute -left-10 top-1/4 bg-white rounded-lg shadow-xl p-3 border border-slate-100 animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-cyan-100 rounded-md flex items-center justify-center">
                      <Clock className="h-4 w-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-900">Time & Attendance</p>
                      <p className="text-[10px] text-slate-500">Clock-in, leave, compliance</p>
                    </div>
                  </div>
                </div>

                <div className="absolute -right-6 bottom-1/4 bg-white rounded-lg shadow-xl p-3 border border-slate-100 animate-float-delayed">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-indigo-100 rounded-md flex items-center justify-center">
                      <Briefcase className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-900">Hiring & Onboarding</p>
                      <p className="text-[10px] text-slate-500">Application to day one</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip — Social proof */}
      <section className="py-4 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '13', label: 'Integrated Modules', icon: Layers },
              { value: '150+', label: 'Sub-Modules', icon: ListChecks },
              { value: 'AI', label: 'Powered Throughout', icon: Brain },
              { value: 'IL-Ready', label: 'TRS / IMRF / SOPPA / BIPA', icon: Shield },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <stat.icon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900 leading-tight">{stat.value}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section — Interactive selector */}
      <section id="modules" className="py-12 bg-slate-900 text-white" style={{ scrollMarginTop: '85px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="bg-blue-900/30 text-blue-400 border-0 mb-3">Comprehensive Solution</Badge>
            <h2 className="text-3xl font-bold text-white mb-2">
              Everything Your District Needs
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              13 integrated modules working together to manage every aspect of district operations — from hiring through retirement, with AI woven throughout.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-5">
            {/* Module Selector — Compact grid */}
            <div className="lg:col-span-4 grid grid-cols-1 gap-1.5">
              {modules.map((module) => (
                <button
                  key={module.id}
                  onClick={() => setActiveModule(module.id)}
                  className={`w-full px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                    activeModule === module.id 
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' 
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <module.icon className={`h-4 w-4 ${activeModule === module.id ? 'text-white' : 'text-slate-400'}`} />
                    <span className="text-sm font-medium">{module.name}</span>
                    <ChevronRight className={`h-3.5 w-3.5 ml-auto ${activeModule === module.id ? 'text-white' : 'text-slate-500'}`} />
                  </div>
                </button>
              ))}
            </div>

            {/* Module Details */}
            <div className="lg:col-span-8 bg-gradient-to-br from-slate-800 to-slate-800/80 rounded-xl p-5 border border-slate-700">
              {activeModuleData && (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-900/40 rounded-xl flex items-center justify-center">
                      <activeModuleData.icon className="h-5 w-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">{activeModuleData.name}</h3>
                      <p className="text-sm text-slate-400">{activeModuleData.description}</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2.5">
                    {activeModuleData.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 p-2.5 bg-slate-700/50 rounded-lg border border-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                        <span className="text-sm text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {ACTIVE_MODULES.includes(activeModuleData.id) && (
                    <Button data-testid="landingpage-learn-more-about-btn" className="bg-blue-600 hover:bg-blue-700 h-9 text-sm"
                      onClick={() => {
                        const sectionId = activeModuleData.id === 'attendance' ? 'time-attendance' : 'hiring-onboarding';
                        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Learn More About {activeModuleData.name}
                      <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* AI-Powered Section — Highlight intelligent capabilities */}
      <section id="ai" className="py-14 bg-gradient-to-br from-indigo-50 via-white to-purple-50 relative overflow-hidden" style={{ scrollMarginTop: '85px' }}>
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-purple-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-indigo-200 rounded-full blur-3xl opacity-30" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-10">
            <Badge className="bg-indigo-100 text-indigo-700 border-0 mb-3 inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" />
              AI-Powered Throughout
            </Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              Intelligence Built Into Every Workflow
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              We don't bolt AI on as a gimmick — it's woven into the core of how districts make decisions every day.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Stethoscope,
                accent: 'from-rose-500 to-red-500',
                title: 'Health Concierge',
                description: 'Personalized health-plan recommendations from staff profile + dependents. Open enrollment becomes 4 questions instead of a 40-page packet.',
              },
              {
                icon: PiggyBank,
                accent: 'from-emerald-500 to-teal-500',
                title: 'Retirement Concierge',
                description: '403(b) / 457(b) contribution recommendations based on tenure, salary, and household. Educates first, asks for input second.',
              },
              {
                icon: TrendingUp,
                accent: 'from-cyan-500 to-blue-500',
                title: 'Smart Savings (Time & Attendance)',
                description: 'Detects overtime patterns, substitute spend anomalies, and chronic absence cost — with a $-impact prediction for every flag.',
              },
              {
                icon: CalendarCheck,
                accent: 'from-amber-500 to-orange-500',
                title: 'Absence Forecasting',
                description: 'Predicts daily absences 3 days out per building and classification. Substitute coordinators stop firefighting and start staffing.',
              },
              {
                icon: FileText,
                accent: 'from-purple-500 to-fuchsia-500',
                title: 'AI Grant Writer',
                description: 'Drafts compliant grant applications in minutes. Pulls district demographics, prior outcomes, and funder priorities automatically.',
              },
              {
                icon: Phone,
                accent: 'from-violet-500 to-indigo-500',
                title: 'SubConnect AI Matching',
                description: 'When traditional sub pools fail, AI matches available substitutes by certification, history, and proximity — auto-fills classrooms.',
              },
              {
                icon: Briefcase,
                accent: 'from-blue-500 to-sky-500',
                title: 'AI HR Tools',
                description: 'Generate job descriptions, evaluation drafts, and policy summaries with one click — tuned to your district\'s voice and CBA.',
              },
              {
                icon: Bus,
                accent: 'from-orange-500 to-red-500',
                title: 'Predictive Fleet Maintenance',
                description: 'Telemetry + mileage + age forecasts which buses need attention before they break down on a route.',
              },
              {
                icon: Brain,
                accent: 'from-slate-700 to-slate-900',
                title: 'AI Concierge (the front door)',
                description: 'Every employee has a single AI assistant that routes them to the right form, policy, or person — across all 13 modules.',
              },
            ].map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div key={i} className="bg-white rounded-xl p-5 border border-slate-200 hover:border-slate-300 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200">
                  <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${feat.accent} flex items-center justify-center mb-3 shadow-sm`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1.5">{feat.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{feat.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built for IL Districts — Compliance Trust Strip */}
      <section id="il-districts" className="py-10 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <Badge className="bg-amber-50 text-amber-800 border-0 mb-2 inline-flex items-center gap-1.5">
              <Award className="h-3 w-3" />
              Built for Illinois School Districts
            </Badge>
            <h2 className="text-2xl font-bold text-slate-900">
              IL-specific compliance — out of the box, not an afterthought
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { code: 'TRS', label: 'Teachers Retirement System', desc: 'Native enrollment + reporting' },
              { code: 'IMRF', label: 'Illinois Municipal Retirement Fund', desc: 'Classified-staff workflows' },
              { code: 'SOPPA', label: '105 ILCS 85', desc: 'Standard DPA & vendor compliance' },
              { code: 'BIPA', label: '740 ILCS 14', desc: 'Biometric consent & audit logs' },
              { code: 'ISBE', label: 'State Board reports', desc: '8 report types, 1-click export' },
              { code: 'FERPA', label: 'Records custodian tools', desc: 'Built-in access controls' },
            ].map((badge, i) => (
              <div key={i} className="bg-slate-50 rounded-lg p-3 border border-slate-100 text-center">
                <div className="text-base font-bold text-slate-900 leading-tight">{badge.code}</div>
                <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{badge.label}</div>
                <div className="text-[11px] text-slate-700 leading-tight mt-1.5 font-medium">{badge.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-3 text-center text-xs text-slate-600">
            <div className="flex items-center gap-2 justify-center">
              <Lock className="h-3.5 w-3.5 text-slate-400" />
              <span>Mandated Reporter (DCFS) tracking</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <Lock className="h-3.5 w-3.5 text-slate-400" />
              <span>Erin's Law training compliance</span>
            </div>
            <div className="flex items-center gap-2 justify-center">
              <Lock className="h-3.5 w-3.5 text-slate-400" />
              <span>IL ROE Rap Back fingerprinting</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose K12 ERP — Compact 2-column */}
      <section id="why-us" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <Badge className="bg-green-50 text-green-700 border-0 mb-3">Why K12 ERP</Badge>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Built Specifically for K-12 Districts
              </h2>
              <p className="text-base text-slate-600">
                A unified platform designed around how school districts actually operate.
              </p>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { icon: Building2, title: 'Multi-Building', description: 'Manage time, staff, and operations across every school from a single platform.' },
                { icon: ClipboardCheck, title: 'Compliance Ready', description: 'Built-in ISBE reporting, FMLA tracking, and union contract rule enforcement.' },
                { icon: Users, title: 'Full Lifecycle', description: 'From application through hiring, onboarding, time tracking, and leave — all connected.' },
                { icon: Shield, title: 'Role-Based Access', description: 'Granular permissions so each role sees only what they need.' },
                { icon: BarChart3, title: 'Live Dashboards', description: 'Real-time visibility into attendance, hiring pipelines, and district metrics.' },
                { icon: Zap, title: 'Less Manual Work', description: 'Drag-and-drop pipelines, automated clock-in, and structured workflows.' },
              ].map((item, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-100 hover:shadow-md hover:border-slate-200 transition-all group">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-2.5 group-hover:bg-blue-600 transition-colors">
                    <item.icon className="h-4.5 w-4.5 text-blue-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Time & Attendance — Feature grid with image */}
      <section id="time-attendance" className="py-12 bg-slate-900 text-white" style={{ scrollMarginTop: '85px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-4">
              <Badge className="bg-cyan-900/30 text-cyan-400 border-cyan-700 mb-3">Time & Attendance</Badge>
              <h2 className="text-2xl font-bold text-white mb-2">
                Comprehensive Time Tracking for K-12
              </h2>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">
                From clock-in to compliance reporting, manage every aspect of staff time across all buildings.
              </p>
              <div className="hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1758685848208-e108b6af94cc?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Time & Attendance"
                  className="rounded-xl w-full object-cover border border-slate-700"
                  style={{ maxHeight: '200px' }}
                />
              </div>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
              {[
                { icon: DollarSign, title: 'Smart Savings Dashboard', description: 'Analyze attendance patterns to identify cost-saving opportunities — overtime trends, substitute spend, leave usage.' },
                { icon: Phone, title: 'Substitute Calling System', description: 'Automated substitute notification and assignment. Track call-outs, acceptances, and fill rates.' },
                { icon: Heart, title: 'FMLA Case Management', description: 'Track FMLA cases from request through resolution — eligibility, hours tracking, compliance docs.' },
                { icon: MapPinned, title: 'Geofencing & Mobile Clock-In', description: 'GPS, PIN, and badge clock-in with geofence verification. Per-building zones and violation flags.' },
                { icon: FileText, title: 'State Compliance Reports', description: 'ISBE-ready reports across 8 types. Export to CSV — attendance summaries, chronic absence, and more.' },
                { icon: ClipboardCheck, title: 'Leave & Exception Pipeline', description: 'Drag-and-drop pipeline for leave requests. Supervisor inbox with approve, deny, escalation.' },
              ].map((feature, index) => (
                <div key={index} className="flex gap-3 p-3.5 bg-slate-800/80 rounded-lg border border-slate-700/80 hover:border-slate-600 transition-colors">
                  <div className="w-9 h-9 bg-cyan-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-0.5">{feature.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Hiring & Onboarding — Horizontal flow */}
      <section id="hiring-onboarding" className="py-12 bg-white" style={{ scrollMarginTop: '85px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-8 order-2 lg:order-1">
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { icon: Users, step: 1, title: 'Hiring Pipeline', description: '7 stages from Application to Hired with drag-and-drop stage management.' },
                  { icon: CalendarCheck, step: 2, title: 'Interview Scheduling', description: 'Schedule with date, time, and location. Reminders sent to all parties.' },
                  { icon: Search, step: 3, title: 'Background Checks', description: 'Multi-component checks with result tracking, flagging, and audit trails.' },
                  { icon: FileSignature, step: 4, title: 'Offer Letters', description: 'Generate offers with position, salary, start date. Track acceptance status.' },
                  { icon: UserCheck, step: 5, title: 'Onboarding Dashboard', description: 'All active onboardings at a glance with progress indicators.' },
                  { icon: ListChecks, step: 6, title: 'New Hire Checklist', description: 'Step-by-step: paperwork, access, equipment, training — status for each.' },
                ].map((feature, index) => (
                  <div key={index} className="flex gap-3 p-3.5 bg-white rounded-lg border border-slate-200 hover:border-indigo-200 hover:shadow-md transition-all">
                    <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-xs">
                      {feature.step}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <feature.icon className="h-3.5 w-3.5 text-indigo-500" />
                        <h3 className="text-sm font-semibold text-slate-900">{feature.title}</h3>
                      </div>
                      <p className="text-xs text-slate-500 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 order-1 lg:order-2">
              <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 mb-3">Hiring & Onboarding</Badge>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Application to Day One
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                A complete hiring pipeline paired with structured onboarding — nothing falls through the cracks.
              </p>
              <div className="hidden lg:block">
                <img 
                  src="https://images.unsplash.com/photo-1758876202980-0a28b744fb24?crop=entropy&cs=srgb&fm=jpg&w=600&q=80"
                  alt="Hiring & Onboarding"
                  className="rounded-xl w-full object-cover border border-slate-200"
                  style={{ maxHeight: '200px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA strip — final conversion */}
      <section className="py-14 bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0, transparent 50%)`,
        }} />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full text-sm font-medium mb-4 border border-white/20">
            <Sparkles size={14} />
            Free 15-minute self-service tool
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 leading-tight">
            See if K12 ERP fits your district —<br className="hidden sm:block"/> in under 15 minutes
          </h2>
          <p className="text-base sm:text-lg text-indigo-100 max-w-2xl mx-auto mb-6">
            Answer 69 structured questions designed by K-12 implementation experts. We'll send you a private link you can share with your IT, HR, and Finance leads — they fill it out together.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button
              size="lg"
              className="h-12 px-7 text-base bg-white text-indigo-700 hover:bg-indigo-50 font-semibold shadow-2xl"
              onClick={() => navigate('/start-discovery')}
              data-testid="bottom-start-discovery-btn"
            >
              Start Free Discovery
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="mailto:info@apsdatatechnologies.com?cc=harish@apsdatatechnologies.com"
               className="inline-flex items-center justify-center h-12 px-7 text-base text-white border border-white/30 hover:bg-white/10 rounded-md font-medium transition-colors">
              Email us instead
            </a>
          </div>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs text-indigo-100/80 flex-wrap">
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> No signup required</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> Auto-saves; pause anytime</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={12} /> IL TRS / IMRF / SOPPA / BIPA aware</span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-3">
                Let's Get In Contact
              </h2>
              <p className="text-base text-slate-300 mb-3">
                Ready to transform your district operations?
              </p>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                See how a unified platform can streamline hiring, time tracking, and operations across your district.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Clock, label: 'Time & Attendance' },
                  { icon: Briefcase, label: 'Hiring & Onboarding' },
                  { icon: Users, label: 'Human Resources' },
                  { icon: DollarSign, label: 'Finance & Payroll' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-5">
              <h3 className="text-base font-semibold mb-3">Request Information</h3>
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 text-green-400 mx-auto mb-3" />
                  <p className="text-white font-medium mb-1">Request Received!</p>
                  <p className="text-sm text-slate-400">We'll be in touch at {contactForm.email} shortly.</p>
                </div>
              ) : (
                <form className="space-y-2.5" onSubmit={handleContactSubmit}>
                  <div className="grid sm:grid-cols-2 gap-2.5">
                    <input data-testid="landingpage-first-name-input" 
                      type="text" 
                      placeholder="First Name"
                      value={contactForm.first_name}
                      onChange={(e) => setContactForm(f => ({ ...f, first_name: e.target.value }))}
                      required
                      className="w-full px-3 py-2.5 bg-slate-700 rounded-lg border border-slate-600 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    />
                    <input data-testid="landingpage-last-name-input" 
                      type="text" 
                      placeholder="Last Name"
                      value={contactForm.last_name}
                      onChange={(e) => setContactForm(f => ({ ...f, last_name: e.target.value }))}
                      required
                      className="w-full px-3 py-2.5 bg-slate-700 rounded-lg border border-slate-600 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <input data-testid="landingpage-work-email-input" 
                    type="email" 
                    placeholder="Work Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                    required
                    className="w-full px-3 py-2.5 bg-slate-700 rounded-lg border border-slate-600 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  />
                  <input data-testid="landingpage-district-name-input" 
                    type="text" 
                    placeholder="District Name"
                    value={contactForm.district_name}
                    onChange={(e) => setContactForm(f => ({ ...f, district_name: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-700 rounded-lg border border-slate-600 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  />
                  <select data-testid="landingpage-contactform-student-count-select" 
                    value={contactForm.student_count}
                    onChange={(e) => setContactForm(f => ({ ...f, student_count: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-slate-700 rounded-lg border border-slate-600 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Number of Students</option>
                    <option value="<1000">Less than 1,000</option>
                    <option value="1000-5000">1,000 - 5,000</option>
                    <option value="5000-15000">5,000 - 15,000</option>
                    <option value="15000+">15,000+</option>
                  </select>
                  <Button data-testid="landingpage-submit-btn" 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 h-10 text-sm"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Request Information'}
                    {!submitting && <ArrowRight className="ml-2 h-3.5 w-3.5" />}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">K</span>
              </div>
              <span className="text-white font-bold text-sm">K12 ERP</span>
            </div>
            <div className="flex items-center gap-6 text-xs">
              <a href="#modules" className="hover:text-white transition-colors">Modules</a>
              <a href="#why-us" className="hover:text-white transition-colors">Why K12 ERP</a>
              <a href="#contact" className="hover:text-white transition-colors">Contact</a>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500">© 2026 K-12 ERP. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom Styles for Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
